import { COLORS, COLOR_ORDER, FACE_ORDER } from './cube-config.js';
import {
  COLOR_TO_FACE,
  CORNER_NAMES,
  CORNER_PIECE_BY_KEY,
  FACE_TO_COLOR,
  OPPOSITE_FACE,
  pieceKey,
} from './cubie-config.js';

const STICKERS_PER_FACE_2X2 = 4;

const CORNER_FACELETS_2X2 = Object.freeze({
  UFR: Object.freeze([['U', 3], ['F', 1], ['R', 0]]),
  URB: Object.freeze([['U', 1], ['R', 1], ['B', 0]]),
  UBL: Object.freeze([['U', 0], ['B', 1], ['L', 0]]),
  ULF: Object.freeze([['U', 2], ['L', 1], ['F', 0]]),
  DRF: Object.freeze([['D', 1], ['R', 2], ['F', 3]]),
  DFL: Object.freeze([['D', 0], ['F', 2], ['L', 3]]),
  DLB: Object.freeze([['D', 2], ['L', 2], ['B', 3]]),
  DBR: Object.freeze([['D', 3], ['B', 2], ['R', 3]]),
});

function error(code, message, details = {}) {
  return { code, message, details };
}

function rotateLeft(value, amount) {
  return value.slice(amount) + value.slice(0, amount);
}

function colorsLabel(faceLetters) {
  return [...faceLetters].map((face) => COLORS[FACE_TO_COLOR[face]].name.replace('色', '')).join('');
}

function containsOppositeFaces(faceLetters) {
  return [...faceLetters].some((face) => faceLetters.includes(OPPOSITE_FACE[face]));
}

function completenessErrors(stickers) {
  if (!stickers || typeof stickers !== 'object') return [error('STICKER_DATA_MISSING', '没有可校验的贴纸数据')];
  const errors = [];
  for (const face of FACE_ORDER) {
    if (!Array.isArray(stickers[face]) || stickers[face].length !== STICKERS_PER_FACE_2X2) {
      errors.push(error('FACE_LENGTH', `${face} 面必须恰好包含 4 张贴纸`, { face }));
    }
  }
  if (errors.length) return errors;
  const all = FACE_ORDER.flatMap((face) => stickers[face]);
  const missing = all.filter((color) => color === null || typeof color === 'undefined').length;
  if (missing) errors.push(error('MISSING_STICKERS', `仍有 ${missing} 张贴纸尚未填写`, { missing }));
  const unknown = all.filter((color) => color !== null && !COLOR_ORDER.includes(color));
  if (unknown.length) errors.push(error('UNKNOWN_COLOR', `检测到 ${unknown.length} 张未知颜色贴纸`));
  const counts = Object.fromEntries(COLOR_ORDER.map((color) => [color, 0]));
  for (const color of all) if (color in counts) counts[color] += 1;
  for (const color of COLOR_ORDER) {
    const count = counts[color];
    if (count === STICKERS_PER_FACE_2X2) continue;
    const difference = Math.abs(STICKERS_PER_FACE_2X2 - count);
    errors.push(error(
      'COLOR_COUNT',
      `${COLORS[color].name}贴纸有 ${count} 张，${count < 4 ? `还缺少 ${difference} 张` : `多出 ${difference} 张`}`,
      { color, count },
    ));
  }
  return errors;
}

export function validateCubeState2x2(stickers) {
  const errors = completenessErrors(stickers);
  if (errors.length) return { valid: false, level: 'completeness', errors };

  const corners = { pieces: [], orientation: [] };
  for (const position of CORNER_NAMES) {
    const observed = CORNER_FACELETS_2X2[position]
      .map(([face, index]) => COLOR_TO_FACE[stickers[face][index]])
      .join('');
    const piece = CORNER_PIECE_BY_KEY[pieceKey(observed)];
    if (typeof piece === 'undefined') {
      errors.push(error(
        'INVALID_CORNER',
        containsOppositeFaces(observed)
          ? `${colorsLabel(observed)}包含相对色，不能位于同一个角块上`
          : `不存在${colorsLabel(observed)}角块`,
        { position, observed },
      ));
      continue;
    }
    const orientation = [0, 1, 2].find((amount) => rotateLeft(CORNER_NAMES[piece], amount) === observed);
    if (typeof orientation === 'undefined') {
      errors.push(error('MIRRORED_CORNER', `${colorsLabel(observed)}角块的贴纸顺序发生镜像，真实魔方无法形成`, { position, observed }));
      continue;
    }
    corners.pieces.push(piece);
    corners.orientation.push(orientation);
  }

  if (!errors.length) {
    const counts = new Map();
    for (const piece of corners.pieces) counts.set(piece, (counts.get(piece) ?? 0) + 1);
    for (let piece = 0; piece < CORNER_NAMES.length; piece += 1) {
      const count = counts.get(piece) ?? 0;
      if (count > 1) errors.push(error('DUPLICATE_CORNER', `检测到重复的${colorsLabel(CORNER_NAMES[piece])}角块`, { piece, count }));
      if (count === 0) errors.push(error('MISSING_CORNER', `缺少${colorsLabel(CORNER_NAMES[piece])}角块`, { piece }));
    }
  }
  if (errors.length) return { valid: false, level: 'pieces', errors };
  if (corners.orientation.reduce((sum, value) => sum + value, 0) % 3 !== 0) {
    return {
      valid: false,
      level: 'reachability',
      errors: [error('CORNER_TWIST', '角块朝向总和不合法，检测到无法由真实转动形成的角块扭转')],
    };
  }
  return { valid: true, level: 'valid', errors: [], patternData: { CORNERS: corners } };
}

export function isSolved2x2(stickers) {
  return FACE_ORDER.every((face) => stickers[face].every((color) => color === FACE_TO_COLOR[face]));
}
